
import React, { useState } from 'react';
import './AdminDashboard.css';
import { useApp, Order } from '../../context/AppContext';
import AddProductModal from '../../components/AddProductModal/AddProductModal';

const AdminDashboard: React.FC = () => {
  const { analytics, orders, products, adminUser, updateOrderStatus, deleteProduct, requestPayout } = useApp();
  const [activeTab, setActiveTab] = useState('overview');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [showAddProduct, setShowAddProduct] = useState(false);

  const pendingOrders = orders.filter(o => o.status === 'pending' || o.status === 'processing');
  const lowStockProducts = products.filter(p => p.stock <= 10);

  const handlePayout = () => {
    const amount = parseFloat(payoutAmount);
    if (amount > 0 && amount <= adminUser.availableBalance) {
      requestPayout(amount);
      setPayoutAmount('');
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-tabs">
        <button className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
          Overview
        </button>
        <button className={`admin-tab ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
          Orders {pendingOrders.length > 0 && <span className="tab-badge">{pendingOrders.length}</span>}
        </button>
        <button className={`admin-tab ${activeTab === 'products' ? 'active' : ''}`} onClick={() => setActiveTab('products')}>
          Products
        </button>
        <button className={`admin-tab ${activeTab === 'payouts' ? 'active' : ''}`} onClick={() => setActiveTab('payouts')}>
          Payouts
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            <div className="stats-grid">
              <div className="stat-card revenue">
                <div className="stat-icon">💰</div>
                <div className="stat-info">
                  <span className="stat-label">Total Revenue</span>
                  <span className="stat-value">${analytics.totalRevenue.toLocaleString()}</span>
                </div>
              </div>
              <div className="stat-card subscribers">
                <div className="stat-icon">👥</div>
                <div className="stat-info">
                  <span className="stat-label">Active Subscribers</span>
                  <span className="stat-value">{analytics.activeSubscribers.toLocaleString()}</span>
                </div>
              </div>
              <div className="stat-card orders">
                <div className="stat-icon">📦</div>
                <div className="stat-info">
                  <span className="stat-label">Total Orders</span>
                  <span className="stat-value">{analytics.totalOrders}</span>
                </div>
              </div>
              <div className="stat-card pending">
                <div className="stat-icon">⏳</div>
                <div className="stat-info">
                  <span className="stat-label">Pending Orders</span>
                  <span className="stat-value">{analytics.pendingOrders}</span>
                </div>
              </div>
            </div>

            <div className="revenue-breakdown">
              <h3>Revenue Breakdown</h3>
              <div className="breakdown-items">
                <div className="breakdown-item">
                  <span className="breakdown-label">Subscriptions</span>
                  <span className="breakdown-value">${analytics.subscriptionRevenue.toLocaleString()}</span>
                  <div className="breakdown-bar" style={{ width: `${(analytics.subscriptionRevenue / analytics.totalRevenue) * 100}%`, background: 'var(--primary)' }} />
                </div>
                <div className="breakdown-item">
                  <span className="breakdown-label">Product Sales</span>
                  <span className="breakdown-value">${analytics.productRevenue.toLocaleString()}</span>
                  <div className="breakdown-bar" style={{ width: `${(analytics.productRevenue / analytics.totalRevenue) * 100}%`, background: 'var(--success)' }} />
                </div>
                <div className="breakdown-item">
                  <span className="breakdown-label">Tips</span>
                  <span className="breakdown-value">${analytics.tipRevenue.toLocaleString()}</span>
                  <div className="breakdown-bar" style={{ width: `${(analytics.tipRevenue / analytics.totalRevenue) * 100}%`, background: 'var(--warning)' }} />
                </div>
              </div>
            </div>

            <div className="top-products">
              <h3>Top Selling Products</h3>
              {analytics.topProducts.map((p, i) => (
                <div key={i} className="top-product">
                  <span className="rank">#{i + 1}</span>
                  <span className="name">{p.name}</span>
                  <span className="sales">{p.sales} sold</span>
                </div>
              ))}
            </div>

            {lowStockProducts.length > 0 && (
              <div className="alerts-section">
                <h3>⚠️ Low Stock Alerts</h3>
                {lowStockProducts.map(p => (
                  <div key={p.id} className="alert-item">
                    <span className="alert-name">{p.name}</span>
                    <span className="alert-stock">{p.stock} left</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="orders-section">
            <h3>Order Management</h3>
            {orders.map(order => (
              <div key={order.id} className="admin-order-card">
                <div className="order-top">
                  <div>
                    <span className="order-id">#{order.id.slice(-6)}</span>
                    <span className="order-date">{order.createdAt.toLocaleDateString()}</span>
                  </div>
                  <span className="order-total">${order.total.toFixed(2)}</span>
                </div>
                <div className="order-items-preview">
                  {order.items.map((item, i) => (
                    <span key={i}>{item.productName} ({item.size}) × {item.quantity}</span>
                  ))}
                </div>
                <div className="order-address">
                  <strong>Ship to:</strong> {order.shippingAddress.name}, {order.shippingAddress.city}
                </div>
                <div className="order-actions">
                  <select 
                    value={order.status} 
                    onChange={e => updateOrderStatus(order.id, e.target.value as Order['status'])}
                    className="status-select"
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'products' && (
          <div className="products-section">
            <div className="section-header">
              <h3>Product Inventory</h3>
              <button className="add-product-btn" onClick={() => setShowAddProduct(true)}>+ Add Product</button>
            </div>
            {products.map(product => (
              <div key={product.id} className="admin-product-card">
                <img src={product.images[0]} alt={product.name} />
                <div className="product-details">
                  <h4>{product.name}</h4>
                  <p className="product-category">{product.category}</p>
                  <div className="product-stats">
                    <span className="price">${product.price}</span>
                    <span className={`stock ${product.stock <= 10 ? 'low' : ''}`}>
                      {product.stock} in stock
                    </span>
                    <span className="sold">{product.sold} sold</span>
                  </div>
                </div>
                <div className="product-actions">
                  <button className="edit-btn">Edit</button>
                  <button className="delete-btn" onClick={() => deleteProduct(product.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'payouts' && (
          <div className="payouts-section">
            <div className="balance-card">
              <h3>Available Balance</h3>
              <span className="balance-amount">${adminUser.availableBalance.toLocaleString()}</span>
              {adminUser.pendingBalance > 0 && (
                <span className="pending-amount">${adminUser.pendingBalance.toLocaleString()} pending</span>
              )}
            </div>

            <div className="payout-form">
              <h3>Request Payout</h3>
              <p className="payout-info">Payouts are sent to: {adminUser.paypalEmail}</p>
              <div className="payout-input-group">
                <span className="currency">$</span>
                <input
                  type="number"
                  placeholder="Amount"
                  value={payoutAmount}
                  onChange={e => setPayoutAmount(e.target.value)}
                  max={adminUser.availableBalance}
                />
              </div>
              <button 
                className="payout-btn" 
                onClick={handlePayout}
                disabled={!payoutAmount || parseFloat(payoutAmount) > adminUser.availableBalance}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.16A.762.762 0 0 1 5.697 1.5h6.49c2.062 0 3.624.534 4.645 1.588.963.998 1.363 2.392 1.188 4.145l-.003.028c-.253 1.612-.916 2.886-1.97 3.787-1.046.895-2.393 1.35-4.003 1.35H9.863l-.698 4.447a.637.637 0 0 1-.63.536H7.076z"/>
                </svg>
                Request PayPal Payout
              </button>
            </div>

            <div className="payout-settings">
              <h3>PayPal Settings</h3>
              <div className="setting-item">
                <label>PayPal Email</label>
                <input type="email" defaultValue={adminUser.paypalEmail} />
              </div>
              <button className="save-settings-btn">Save Settings</button>
            </div>
          </div>
        )}
      </div>

      {showAddProduct && <AddProductModal onClose={() => setShowAddProduct(false)} />}
    </div>
  );
};

export default AdminDashboard;
